'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { DiscoveryPageLayout } from '@/domains/paydirt/layout';
import { paydirtConfig } from '@/config/paydirt';
import { getRankings, type RankingEntry } from '@/domains/paydirt/rankings/api';

export function LeaderboardPage() {
  const [entries, setEntries] = useState<RankingEntry[]>([]);
  const [computedAt, setComputedAt] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    getRankings({ period: 'all_time', metric: 'roi' })
      .then((doc) => {
        if (cancelled) return;
        setEntries(doc.entries ?? []);
        setComputedAt(doc.computedAt);
      })
      .catch(() => {
        if (!cancelled) {
          setEntries([]);
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const leaderboard = paydirtConfig.leaderboard;

  return (
    <DiscoveryPageLayout
      title={leaderboard.label}
      description={leaderboard.description}
    >
      {loading ? (
        <p className="text-muted" role="status">
          Loading leaderboard…
        </p>
      ) : error ? (
        <p className="text-muted">Leaderboard data is not available yet.</p>
      ) : entries.length === 0 ? (
        <p className="text-muted">No ranked haul reports yet. Publish a claim to compete on ROI.</p>
      ) : (
        <>
          {computedAt && (
            <p className="text-muted leaderboard-updated">
              Updated {new Date(computedAt).toLocaleString()}
            </p>
          )}
          <ol className="leaderboard-list" data-testid="leaderboard-list">
            {entries.map((entry, index) => {
              const roi = entry.roiPct ?? entry.score;
              return (
                <li key={entry.claimId} className="leaderboard-list__item">
                  <span className="leaderboard-list__rank" aria-label={`Rank ${index + 1}`}>
                    {index + 1}
                  </span>
                  <div className="leaderboard-list__body">
                    <Link href={`/claims/${entry.claimId}/`} className="leaderboard-list__title">
                      Haul report
                    </Link>
                    <span className="leaderboard-list__roi">ROI {roi.toFixed(1)}%</span>
                  </div>
                </li>
              );
            })}
          </ol>
        </>
      )}
    </DiscoveryPageLayout>
  );
}
